import type { ProductionHistoryWindow } from "@/features/devices/types";
import { bucketMsForWindow } from "../../chartWidgets";
import type { ChartKind, WidgetThreshold } from "../../types";
import { formatWidgetNumber } from "./format";

/*
 * Geometria PURA do grafico temporal: recebe pontos ja projetados e o tamanho
 * em pixels, devolve coordenadas prontas para o SVG e para o cursor. Nao toca
 * o DOM nem o React — e o que permite memoizar o desenho e responder ao mouse
 * sem recalcular 1.440 pontos a cada movimento.
 */

export interface TimeSeriesPoint {
  at: string;
  /**
   * `null` = não há leitura utilizável neste instante. A linha INTERROMPE e a
   * barra vira marca discreta na base. Ver `unavailable` para a razão.
   */
  value: number | null;
  /** Prova de descontinuidade fornecida pelo backend, mesmo entre buckets vizinhos. */
  breakBefore?: boolean;
  /**
   * Lacuna antes da leitura. Nao prova producao durante o trecho ausente.
   */
  gap?: boolean;
  /** Somente a projecao de DELTA pode declarar incremento atraves de lacuna. */
  includesGapDelta?: boolean;
  /**
   * Intervalo com produção DESCONHECIDA (gap de telemetria): não empilha valor
   * — renderiza como marca discreta na base, sem inflar a escala.
   */
  unknown?: boolean;
  /**
   * Por que não há valor. `failed` = o equipamento foi lido e não respondeu
   * (falha AIN / transação Modbus fora de `ok`); `missing` = não houve
   * telemetria. Muda só o texto do tooltip — a linha interrompe nos dois.
   */
  unavailable?: "failed" | "missing";
  /** Fim do intervalo coberto (bins de barras). Ausente = um bucket da janela. */
  until?: string;
  /** Extremos dentro do bucket (serie de ritmo). A media esconde a variacao. */
  min?: number | null;
  max?: number | null;
}

/**
 * Série adicional plotada no MESMO eixo. Mesma escolha de forma do modelo de
 * widget: a série âncora continua em `points` e as demais entram à parte, para
 * que todo chamador existente siga funcionando sem alteração.
 *
 * O contrato de quem passa `extraSeries` é ter validado que as unidades e o
 * `kind` batem (`canCombineSources`) — este componente desenha o que recebe e
 * não tem como saber se duas escalas fazem sentido juntas.
 */
export interface TimeSeriesExtraSeries {
  points: TimeSeriesPoint[];
  label: string;
  /**
   * Slot de COR desta serie. Explicito, e nao a posicao no array, porque quem
   * chama filtra as series sem pontos antes de passar: usar a posicao aqui
   * fazia a cor da linha divergir da cor do swatch na legenda, que enumera as
   * fontes TODAS. O resultado era a legenda apontar para a fase errada.
   */
  colorIndex: number;
}

/** Linha horizontal de referencia (ex.: media do periodo). */
export interface TimeSeriesReference {
  value: number;
  label: string;
}

export interface ChartSize {
  w: number;
  h: number;
}

/**
 * Tamanho usado quando o elemento ainda nao foi medido (primeiro frame, ou
 * ambiente sem layout como o jsdom dos testes). E o antigo viewBox fixo, e os
 * testes que conferem coordenadas dependem dele.
 */
export const FALLBACK_SIZE: ChartSize = { w: 640, h: 200 };

const PAD_R = 10;
const PAD_B = 24;
/** Largura aproximada de um caractere do rotulo (10px, tabular). */
const LABEL_CHAR_PX = 6;

export interface PlottedPoint {
  x: number;
  /** `null` quando o ponto nao tem valor: o cursor ainda para nele. */
  y: number | null;
  point: TimeSeriesPoint;
}

export interface PlottedSeries {
  label: string;
  colorIndex: number;
  segments: string[];
  byTime: Map<number, PlottedPoint>;
}

export interface PlottedBar {
  from: number;
  to: number;
  x: number;
  y: number;
  w: number;
  h: number;
  index: number;
  point: TimeSeriesPoint;
  afterGap: boolean;
  /** Sem valor utilizavel: marca discreta na base. */
  blank: boolean;
}

export interface ChartGeometry {
  w: number;
  h: number;
  padL: number;
  padT: number;
  plotW: number;
  plotH: number;
  zeroY: number;
  domain: { lo: number; hi: number; span: number };
  timeDomain: TimeDomain;
  bucketMs: number;
  yTicks: Array<{ y: number; label: string }>;
  xTicks: Array<{ x: number; label: string; anchor: "start" | "middle" | "end" }>;
  thresholds: Array<{ y: number; tone: Exclude<WidgetThreshold["tone"], "neutral"> }>;
  reference: { y: number; label: string; labelAbove: boolean } | null;
  /** Linha: ancora em [0], extras na sequencia. Vazio em barras. */
  series: PlottedSeries[];
  /** Instantes distintos de TODAS as series, ordenados. Vazio em barras. */
  times: number[];
  bars: PlottedBar[];
  lastDot: { x: number; y: number } | null;
}

export interface GeometryInput {
  points: TimeSeriesPoint[];
  extraSeries: TimeSeriesExtraSeries[];
  anchorLabel: string;
  kind: ChartKind;
  window: ProductionHistoryWindow;
  unit?: string;
  decimals: number;
  yScale: "auto" | "fixed";
  min?: number;
  max?: number;
  thresholds: WidgetThreshold[];
  reference?: TimeSeriesReference | null;
  size: ChartSize;
  /** Periodo explicito de calendario; nao transforma uma janela movel em turno. */
  period?: { startAt: string; endAt: string; timeZone: string; resolutionMs: number };
}

export function buildChartGeometry(input: GeometryInput): ChartGeometry {
  const { points, extraSeries, kind, window, size } = input;
  const w = size.w;
  const h = size.h;
  const allSeries = [points, ...extraSeries.map((serie) => serie.points)];

  // A escala ignora os buracos: um período sem leitura não pode encolher nem
  // esticar o eixo dos períodos que têm leitura. Com N séries o domínio cobre
  // TODAS — senão a segunda linha sai do quadro ou fica achatada no chão.
  const values = allSeries.flatMap((serie) =>
    serie.flatMap((point) => (point.value === null || point.unknown ? [] : [point.value])),
  );
  const domain = resolveDomain(values, kind, input.yScale, input.min, input.max);
  const yTickValues = [domain.lo, (domain.lo + domain.hi) / 2, domain.hi];
  const yLabels = yTickValues.map((tick) => formatWidgetNumber(tick, input.decimals));

  // Margem esquerda pelo rotulo mais largo: "22.823,8" nao cabia nos 46px
  // fixos e vazava para fora do card. A unidade sobe para a propria linha,
  // acima do tick superior — antes as duas se sobrepunham.
  const longest = yLabels.reduce((max, label) => Math.max(max, label.length), 0);
  const padL = Math.max(34, longest * LABEL_CHAR_PX + 12);
  const padT = input.unit ? 22 : 12;
  const plotW = Math.max(w - padL - PAD_R, 1);
  const plotH = Math.max(h - padT - PAD_B, 1);
  const yToPx = (value: number) => padT + plotH - ((value - domain.lo) / domain.span) * plotH;
  const zeroY = yToPx(Math.max(domain.lo, Math.min(domain.hi, 0)));

  // Eixo X por TEMPO, nao por indice de array: duas fontes com contagens de
  // bucket diferentes no mesmo intervalo real precisam coincidir no X. O
  // backend nao faz gap-fill e `canCombineSources` nem exige mesmo device.
  const bucketMs = input.period?.resolutionMs ?? bucketMsForWindow(window);
  const timeDomain = input.period ? { from: Date.parse(input.period.startAt), to: Date.parse(input.period.endAt) } : resolveTimeDomain(allSeries, kind === "bar" ? bucketMs : undefined);
  const xAt = (at: string) => xForTime(at, timeDomain, padL, plotW);

  const thresholds = input.thresholds
    .filter((threshold) => threshold.at >= domain.lo && threshold.at <= domain.hi)
    .map((threshold) => ({ y: yToPx(threshold.at), tone: threshold.tone }));

  const reference =
    input.reference && input.reference.value >= domain.lo && input.reference.value <= domain.hi
      ? (() => {
          const y = yToPx(input.reference.value);
          return { y, label: input.reference.label, labelAbove: y > padT + 14 };
        })()
      : null;

  const geometry: ChartGeometry = {
    w,
    h,
    padL,
    padT,
    plotW,
    plotH,
    zeroY,
    domain,
    timeDomain,
    bucketMs,
    yTicks: yTickValues.map((tick, index) => ({ y: yToPx(tick), label: yLabels[index] })),
    xTicks: [],
    thresholds,
    reference,
    series: [],
    times: [],
    bars: [],
    lastDot: null,
  };

  if (kind === "bar") {
    points.forEach((point, sourceIndex) => {
      const from = Date.parse(point.at);
      const to = point.until ? Date.parse(point.until) : from + bucketMs;
      if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from || to <= timeDomain.from || from >= timeDomain.to) return;
      const left = xAt(point.at);
      const band = xAt(new Date(to).toISOString()) - left;
      const innerGap = Math.min(band * 0.2, 6);
      const x = left + innerGap / 2;
      const width = band - innerGap;
      const index = geometry.bars.length;
      const blank = point.unknown === true || point.value === null;
      if (blank) {
        geometry.bars.push({ from, to, x, y: Math.max(padT, zeroY - 5), w: width, h: 5, index, point, afterGap: false, blank });
        return;
      }
      const value = point.value as number;
      const top = yToPx(Math.max(domain.lo, Math.min(domain.hi, value)));
      const height = Math.max(Math.abs(zeroY - top), 1);
      const previous = points[sourceIndex - 1];
      const previousEnd = previous ? (previous.until ? Date.parse(previous.until) : Date.parse(previous.at) + bucketMs) : from;
      const afterGap = point.gap ?? from > previousEnd;
      geometry.bars.push({
        from, to,
        x,
        y: Math.min(Math.min(top, zeroY), padT + plotH - height),
        w: width,
        h: height,
        index,
        point,
        afterGap,
        blank,
      });
    });
    geometry.xTicks = timeTicks(timeDomain, padL, plotW, window, input.period?.timeZone);
    return geometry;
  }

  const timeSet = new Set<number>();
  const plotSeries = (serie: TimeSeriesPoint[], label: string, colorIndex: number): PlottedSeries => {
    const byTime = new Map<number, PlottedPoint>();
    for (const point of serie) {
      const at = Date.parse(point.at);
      if (!Number.isFinite(at)) continue;
      timeSet.add(at);
      byTime.set(at, { x: xAt(point.at), y: point.value === null ? null : yToPx(point.value), point });
    }
    return { label, colorIndex, segments: buildLineSegments(serie, bucketMs, xAt, yToPx), byTime };
  };
  geometry.series = [
    plotSeries(points, input.anchorLabel, 0),
    ...extraSeries.map((serie) => plotSeries(serie.points, serie.label, serie.colorIndex)),
  ];
  geometry.times = [...timeSet].sort((a, b) => a - b);

  const lastPoint = points.at(-1);
  if (lastPoint && lastPoint.value !== null && extraSeries.length === 0) {
    geometry.lastDot = { x: xAt(lastPoint.at), y: yToPx(lastPoint.value) };
  }

  // Rótulos derivados do domínio de TEMPO, para valerem para todas as séries.
  geometry.xTicks = timeTicks(timeDomain, padL, plotW, window, input.period?.timeZone);
  return geometry;
}

function timeTicks(timeDomain: TimeDomain, padL: number, plotW: number, window: ProductionHistoryWindow, timeZone?: string): ChartGeometry["xTicks"] {
  const count = tickCountFor(plotW);
  return Array.from({ length: count }, (_, position) => {
    const fraction = count === 1 ? 0 : position / (count - 1);
    const at = new Date(timeDomain.from + (timeDomain.to - timeDomain.from) * fraction).toISOString();
    return {
      x: padL + fraction * plotW,
      label: formatChartTime(at, window, timeZone),
      anchor: position === 0 ? "start" : position === count - 1 ? "end" : "middle",
    };
  });
}

/** Quantos rotulos de tempo cabem sem se atropelar (cada um ~40px). */
function tickCountFor(plotW: number): number {
  if (plotW >= 520) return 5;
  if (plotW >= 340) return 4;
  return 3;
}

/** Instante (ms) sob a coordenada X do plot, limitado ao dominio. */
export function timeAtX(geometry: ChartGeometry, x: number): number {
  const fraction = Math.min(1, Math.max(0, (x - geometry.padL) / geometry.plotW));
  return geometry.timeDomain.from + (geometry.timeDomain.to - geometry.timeDomain.from) * fraction;
}

/** Indice em `times` do instante mais proximo (busca binaria). -1 se vazio. */
export function nearestTimeIndex(times: number[], target: number): number {
  if (times.length === 0) return -1;
  let lo = 0;
  let hi = times.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(times[lo - 1] - target) <= Math.abs(times[lo] - target)) return lo - 1;
  return lo;
}

/** Indice da barra sob a coordenada X. -1 fora do plot ou sem barras. */
export function barIndexAtX(geometry: ChartGeometry, x: number): number {
  if (x < geometry.padL || x >= geometry.padL + geometry.plotW) return -1;
  const time = timeAtX(geometry, x);
  return geometry.bars.findIndex(bar => time >= bar.from && time < bar.to);
}

export interface TimeDomain {
  from: number;
  to: number;
}

/**
 * Menor e maior instante entre TODAS as series. Series vazias nao contribuem.
 * Quando nao ha instante utilizavel (ou todos sao iguais), devolve um dominio
 * degenerado que `xForTime` resolve no centro do plot.
 */
function resolveTimeDomain(seriesList: TimeSeriesPoint[][], bucketMs?: number): TimeDomain {
  let from = Number.POSITIVE_INFINITY;
  let to = Number.NEGATIVE_INFINITY;
  for (const serie of seriesList) {
    for (const point of serie) {
      const at = Date.parse(point.at);
      if (!Number.isFinite(at)) continue;
      const end = bucketMs ? (point.until ? Date.parse(point.until) : at + bucketMs) : at;
      if (!Number.isFinite(end) || (bucketMs && end <= at)) continue;
      if (at < from) from = at;
      if (end > to) to = end;
    }
  }
  return Number.isFinite(from) && Number.isFinite(to) ? { from, to } : { from: 0, to: 0 };
}

function xForTime(at: string, domain: TimeDomain, padL: number, plotW: number): number {
  const span = domain.to - domain.from;
  if (span <= 0) return padL + plotW / 2;
  const parsed = Date.parse(at);
  if (!Number.isFinite(parsed)) return padL;
  const fraction = Math.min(1, Math.max(0, (parsed - domain.from) / span));
  return padL + fraction * plotW;
}

/**
 * Quebra a série em segmentos contínuos. As quebras dizem "aqui eu não sei":
 *
 *  1. ponto sem valor (`value === null`): leitura que falhou ou bucket vazio;
 *  2. salto de tempo entre buckets consecutivos (`isGapBefore`): a série pulou
 *     um pedaço da janela, então ligar os vizinhos inventaria uma reta que
 *     ninguém mediu.
 *  3. breakBefore: evidencia explicita do servidor, mesmo em buckets vizinhos.
 *
 * Um ponto isolado entre dois buracos vira um segmento de um ponto só — que o
 * SVG não desenha. Por isso ele recebe um `M`+`L` no mesmo lugar: um traço
 * mínimo é melhor que sumir com a única leitura boa do intervalo.
 */
function buildLineSegments(
  points: TimeSeriesPoint[],
  bucketMs: number,
  xAt: (at: string) => number,
  yToPx: (value: number) => number,
): string[] {
  const segments: string[] = [];
  let current: string[] = [];

  const flush = () => {
    if (current.length === 0) return;
    segments.push(current.length === 1 ? `${current[0]} L${current[0].slice(1)}` : current.join(" "));
    current = [];
  };

  points.forEach((point, index) => {
    if (point.value === null) {
      flush();
      return;
    }
    const previous = points[index - 1];
    const brokenByTime =
      previous !== undefined && previous.value !== null && Date.parse(point.at) - Date.parse(previous.at) > bucketMs * 1.8;
    if (brokenByTime || point.breakBefore) flush();

    const command = current.length === 0 ? "M" : "L";
    current.push(`${command}${xAt(point.at).toFixed(1)},${yToPx(point.value).toFixed(1)}`);
  });

  flush();
  return segments;
}

function resolveDomain(
  values: number[],
  kind: ChartKind,
  yScale: "auto" | "fixed",
  min?: number,
  max?: number,
): { lo: number; hi: number; span: number } {
  let lo: number;
  let hi: number;
  if (yScale === "fixed" && typeof min === "number" && typeof max === "number" && max > min) {
    lo = min;
    hi = max;
  } else {
    const dataMin = values.length > 0 ? Math.min(...values) : 0;
    const dataMax = values.length > 0 ? Math.max(...values) : 1;
    // Barras crescem do zero; linha pode partir do mínimo observado.
    lo = kind === "bar" ? Math.min(0, dataMin) : dataMin;
    hi = kind === "bar" ? Math.max(0, dataMax) : dataMax;
  }
  const span = Math.max(hi - lo, 1e-9);
  return { lo, hi, span };
}

export function formatChartTime(iso: string | undefined, window: ProductionHistoryWindow, timeZone?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  if (timeZone) return new Intl.DateTimeFormat("pt-BR", { timeZone, hour: "2-digit", minute: "2-digit" }).format(date);
  if (window === "7d" || window === "30d") {
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
  }
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const pad = (value: number) => String(value).padStart(2, "0");
const dayOf = (date: Date) => `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
const clockOf = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

/** Instante solto (cursor numa lacuna): "13/09 · 05:31". */
export function describeChartInstant(at: string, timeZone?: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return "";
  if (timeZone) return new Intl.DateTimeFormat("pt-BR", { timeZone, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
  return `${dayOf(date)} · ${clockOf(date)}`;
}

/**
 * Descreve o intervalo que um ponto/barra cobre, para o tooltip. Diz o que o
 * bucket E — um minuto, uma hora, um dia ou um bin de varias horas — porque
 * "14:00" sozinho esconderia que o valor e a media da hora inteira.
 */
export function describeChartInterval(
  at: string,
  until: string | undefined,
  window: ProductionHistoryWindow,
  timeZone?: string,
): string {
  const start = new Date(at);
  if (Number.isNaN(start.getTime())) return "";
  const bucketMs = bucketMsForWindow(window);
  const end = new Date(until ? Date.parse(until) : start.getTime() + bucketMs);
  if (timeZone) return `${describeChartInstant(at, timeZone)} – ${describeChartInstant(end.toISOString(), timeZone)}`;
  const spanMs = end.getTime() - start.getTime();

  if (!until && bucketMs === 60_000) return `${dayOf(start)} · ${clockOf(start)}`;
  if (!until && bucketMs >= 86_400_000) return dayOf(start);
  if (Number.isNaN(end.getTime()) || spanMs <= 0) return `${dayOf(start)} · ${clockOf(start)}`;
  if (spanMs >= 86_400_000 && until) {
    // Bins de dias inteiros: "13/09 – 15/09".
    const lastDay = new Date(end.getTime() - 1);
    return dayOf(start) === dayOf(lastDay) ? dayOf(start) : `${dayOf(start)} – ${dayOf(lastDay)}`;
  }
  const sameDay = dayOf(start) === dayOf(end) || (end.getHours() === 0 && end.getMinutes() === 0 && spanMs <= 86_400_000);
  return sameDay
    ? `${dayOf(start)} · ${clockOf(start)}–${clockOf(end)}`
    : `${dayOf(start)} ${clockOf(start)} – ${dayOf(end)} ${clockOf(end)}`;
}
